import dayjs from "dayjs";

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const show = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text ? escapeHtml(text) : "-";
};

const formatDate = (value: unknown) => {
  if (!value) return "-";
  const parsed = dayjs(String(value).split("T")[0]);
  return parsed.isValid() ? parsed.format("DD.MM.YYYY") : show(value);
};

const imageSrc = (value: unknown) => {
  const src = String(value ?? "");
  return src.startsWith("data:image/") ? src : "";
};

const formatRh = (value: unknown) => {
  const text = String(value ?? "").trim();
  if (text === "+" || /^pos/i.test(text)) return "Positive";
  if (text === "-" || /^neg/i.test(text)) return "Negative";
  return text;
};

const splitBloodGroup = (donor: any) => {
  const raw = String(donor?.bloodGroup ?? "").trim().toUpperCase();
  const match = raw.match(/^(AB|A|B|O)\s*([+-])?$/);
  const abo = match ? match[1] : raw;
  const rh = formatRh(match?.[2] || donor?.rhFactor);
  return { abo, rh };
};

const pxPerMm = (doc: Document) => {
  const probe = doc.createElement("div");
  probe.style.cssText = "position:absolute;height:100mm;width:0;visibility:hidden;";
  doc.body.appendChild(probe);
  const value = probe.getBoundingClientRect().height / 100;
  probe.remove();
  return value || 3.7795;
};

const paginateDonorPrint = (doc: Document) => {
  const source = doc.getElementById("source");
  const pages = doc.getElementById("pages");
  const headerTemplate = doc.getElementById("header-template");
  if (!source || !pages || !headerTemplate) return;

  const mm = (value: number) => value * pxPerMm(doc);
  const pageContentWidth = mm(210 - 16);
  const pageContentHeight = mm(297 - 16);

  const measureHost = doc.createElement("div");
  measureHost.id = "measure-host";
  measureHost.style.width = `${pageContentWidth}px`;
  doc.body.appendChild(measureHost);

  const heightOf = (node: HTMLElement) => {
    measureHost.appendChild(node);
    const height = node.offsetHeight;
    measureHost.removeChild(node);
    return height;
  };

  const headerProbe = doc.createElement("div");
  headerProbe.className = "page-header";
  headerProbe.innerHTML = headerTemplate.innerHTML;
  const available = pageContentHeight - heightOf(headerProbe) - mm(8);

  const buildTableBlock = (
    className: string,
    title: string | null,
    theadHtml: string,
    rows: HTMLElement[]
  ) => {
    const block = doc.createElement("div");
    block.className = "block";
    if (title) {
      const heading = doc.createElement("h2");
      heading.textContent = title;
      block.appendChild(heading);
    }
    const table = doc.createElement("table");
    table.className = className;
    if (theadHtml) {
      table.insertAdjacentHTML("afterbegin", `<thead>${theadHtml}</thead>`);
    }
    const tbody = doc.createElement("tbody");
    rows.forEach((row) => tbody.appendChild(row));
    table.appendChild(tbody);
    block.appendChild(table);
    return block;
  };

  const measuredTableHeight = (
    className: string,
    title: string | null,
    theadHtml: string,
    rows: HTMLElement[]
  ) => {
    const shell = buildTableBlock(className, title, theadHtml, rows);
    const height = heightOf(shell);
    const body = shell.querySelector("tbody");
    while (body?.firstChild) body.removeChild(body.firstChild);
    return height;
  };

  const stackedHeight = (nodes: HTMLElement[]) => {
    const wrap = doc.createElement("div");
    wrap.className = "page-body";
    nodes.forEach((node) => wrap.appendChild(node));
    const height = heightOf(wrap);
    while (wrap.firstChild) wrap.removeChild(wrap.firstChild);
    return height;
  };

  const sheets: HTMLElement[][] = [];
  let current: HTMLElement[] = [];
  const closePage = () => {
    if (!current.length) return;
    sheets.push(current);
    current = [];
  };
  const remaining = () => (current.length ? available - stackedHeight(current) : available);

  Array.from(source.children).forEach((node) => {
    const block = node as HTMLElement;
    const table = block.querySelector("table");
    const canSplit = block.dataset.split === "rows" && !!table;
    const blockHeight = heightOf(block);

    if (!canSplit || blockHeight <= remaining()) {
      if (current.length && blockHeight > remaining()) closePage();
      current.push(block);
      return;
    }

    const title = block.querySelector("h2")?.textContent?.trim() || null;
    const theadHtml = table?.querySelector("thead")?.innerHTML || "";
    const className = table?.className || "";
    let pending = Array.from(table?.tBodies[0]?.rows || []) as HTMLElement[];
    let isFirst = true;

    while (pending.length) {
      if (current.length && remaining() < mm(12)) closePage();
      const room = remaining();
      let take: HTMLElement[] = [];
      for (let index = 0; index < pending.length; index += 1) {
        const trial = [...take, pending[index]];
        const height = measuredTableHeight(className, isFirst ? title : null, theadHtml, trial);
        if (take.length > 0 && height > room) break;
        if (!take.length && height > room && current.length > 0) break;
        take = trial;
        if (height > room) break;
      }
      if (!take.length) {
        if (current.length) {
          closePage();
          continue;
        }
        take = [pending[0]];
      }
      current.push(buildTableBlock(className, isFirst ? title : null, theadHtml, take));
      pending = pending.slice(take.length);
      isFirst = false;
      if (pending.length) closePage();
    }
  });
  closePage();

  const headerHeight = heightOf(headerProbe);
  for (let index = 0; index < sheets.length; index += 1) {
    const bodyNodes = sheets[index];
    while (
      bodyNodes.length > 1 &&
      headerHeight + stackedHeight(bodyNodes) > pageContentHeight - mm(3)
    ) {
      const overflow = bodyNodes.pop();
      if (!overflow) break;
      if (!sheets[index + 1]) sheets.push([]);
      sheets[index + 1].unshift(overflow);
    }
  }

  sheets.forEach((sheetChunks) => {
    const sheet = doc.createElement("section");
    sheet.className = "sheet";
    const header = doc.createElement("div");
    header.className = "page-header";
    header.innerHTML = headerTemplate.innerHTML;
    const body = doc.createElement("div");
    body.className = "page-body";
    sheetChunks.forEach((chunk) => body.appendChild(chunk));
    sheet.appendChild(header);
    sheet.appendChild(body);
    pages.appendChild(sheet);
  });

  if (pages.childElementCount > 0) source.remove();
  headerTemplate.remove();
  measureHost.remove();
};

export const printDonorWithConsent = (
  donor: any,
  header?: { title?: string; address?: string; phone?: string; logo?: string }
) => {
  const vitals = donor?.vitals || {};
  const bloodGroup = splitBloodGroup(donor);
  const history = Array.isArray(donor?.medicalHistory) ? donor.medicalHistory : [];
  const historyPairs: string[] = [];
  for (let i = 0; i < history.length; i += 2) {
    const left = history[i];
    const right = history[i + 1];
    historyPairs.push(`
      <tr>
        <td>${show(left?.question)}</td>
        <td class="yn">${left?.answer ? "Yes" : "No"}</td>
        ${
          right
            ? `<td>${show(right?.question)}</td><td class="yn">${right?.answer ? "Yes" : "No"}</td>`
            : `<td></td><td></td>`
        }
      </tr>`);
  }
  const historyRows = historyPairs.length
    ? historyPairs.join("")
    : `<tr><td colspan="4">No medical history</td></tr>`;

  const donorSign = imageSrc(donor?.donorSignature);
  const doctorSign = imageSrc(donor?.doctorSignature);
  const logo = header?.logo ? escapeHtml(header.logo) : "";

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Donor Consent</title>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap" />
  <style>
    @page { size: A4; margin: 8mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; color: #111; font-size: 12px; margin: 0; }
    #header-template { position: absolute; left: -10000px; top: 0; width: 194mm; }
    #measure-host { position: absolute; left: -10000px; top: 0; visibility: hidden; }
    .sheet { break-after: page; page-break-after: always; }
    .sheet:last-child { break-after: auto; page-break-after: auto; }
    .page-header { text-align: center; padding-bottom: 4px; }
    .page-header img { height: 48px; }
    .page-body { display: flow-root; }
    .block { display: flow-root; break-inside: avoid; page-break-inside: avoid; }
    h1 { font-size: 18px; margin: 0; text-align: center; }
    .sub { text-align: center; font-size: 11px; margin-top: 2px; }
    h2 {
      font-size: 13px;
      margin: 12px 0 4px;
      padding: 0;
      text-align: left;
      font-weight: 700;
      background: transparent;
      color: #000;
      break-after: avoid;
      page-break-after: avoid;
    }
    .grid, table.history, table.deferral { width: 100%; border-collapse: collapse; }
    .grid td { border: 1px solid #c5d0dc; padding: 5px 7px; vertical-align: top; background: #fff; }
    .cols-5 td { width: 20%; }
    .cols-3 td { width: 33.33%; }
    .cols-2 td { width: 50%; }
    .label { font-weight: 700; display: block; font-size: 9px; color: #5f6b7a; text-transform: uppercase; letter-spacing: 0.2px; }
    .value { display: block; margin-top: 2px; font-size: 12px; font-weight: 600; color: #111; }
    table.history, table.deferral { margin-top: 0; }
    table.history th, table.history td,
    table.deferral th, table.deferral td { border: 1px solid #c5d0dc; padding: 4px 6px; text-align: left; vertical-align: top; }
    table.history th, table.deferral th { background: #e8eef5; font-size: 11px; }
    table.history th.yn, table.history td.yn,
    table.deferral td.num, table.deferral th.num { width: 36px; text-align: center; white-space: nowrap; }
    table.history th.yn { width: 52px; }
    .grid tr, table.history tr, table.deferral tr, .consent, .signs, .sign, .urdu, .remarks {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .remarks {
      margin-top: 10px;
      border: 1px solid #c5d0dc;
      padding: 8px 10px;
      font-size: 13px;
    }
    .remarks strong { margin-right: 6px; }
    .fit { color: #0d7a32; font-weight: 700; }
    .unfit { color: #b42318; font-weight: 700; }
    td, .consent { overflow-wrap: break-word; }
    .consent { border: 1px solid #d0d0d0; border-radius: 6px; padding: 10px; margin-top: 8px; }
    .urdu {
      font-family: "Noto Nastaliq Urdu", "NotoNastaliqUrdu", serif;
      direction: rtl;
      text-align: right;
      line-height: 2;
      font-size: 15px;
      margin-top: 8px;
      white-space: nowrap;
    }
    .signs { display: flex; gap: 16px; margin-top: 12px; }
    .sign { flex: 1; }
    .box { border: 1px solid #111; height: 110px; display: flex; align-items: center; justify-content: center; }
    .box img { max-width: 100%; max-height: 100px; }
    .caption { font-weight: 700; margin-bottom: 4px; }
    @media print {
      #header-template, #measure-host { display: none !important; }
    }
  </style>
</head>
<body>
  <div id="header-template">
    ${logo ? `<div><img src="${logo}" alt="" /></div>` : ""}
    <h1>${show(header?.title || "Donor Details")}</h1>
    <div class="sub">${show(header?.address)}${header?.phone ? ` (Ph: ${show(header.phone)})` : ""}</div>
  </div>
  <div id="source">
  <div class="block" data-split="rows">
  <h2>DONOR DETAILS</h2>
  <table class="grid cols-5">
    <tr>
      <td><span class="label">Donor ID</span><span class="value">${show(donor?.donorId)}</span></td>
      <td><span class="label">Donor Name</span><span class="value">${show(donor?.name)}</span></td>
      <td><span class="label">Father Name</span><span class="value">${show(donor?.fatherName)}</span></td>
      <td><span class="label">Gender</span><span class="value">${show(donor?.gender)}</span></td>
      <td><span class="label">Age</span><span class="value">${show(donor?.age)}</span></td>
    </tr>
    <tr>
      <td><span class="label">Occupation</span><span class="value">${show(donor?.occupation)}</span></td>
      <td><span class="label">Cell No</span><span class="value">${show(donor?.cellNo)}</span></td>
      <td><span class="label">CNIC</span><span class="value">${show(donor?.cnic)}</span></td>
      <td><span class="label">Address</span><span class="value">${show(donor?.city)}</span></td>
      <td><span class="label">Weight</span><span class="value">${vitals?.weight ? `${show(vitals.weight)} KG` : "-"}</span></td>
    </tr>
    <tr>
      <td><span class="label">General Physical Examination</span><span class="value">${show(donor?.generalPhysicalAppearance)}</span></td>
      <td><span class="label">Donation Type</span><span class="value">${show(donor?.donationType)}</span></td>
      <td><span class="label">Last Donation Date</span><span class="value">${formatDate(donor?.lastDonationDate)}</span></td>
      <td><span class="label">Center of Donation</span><span class="value">${show(donor?.centerOfDonation)}</span></td>
      <td><span class="label">Date</span><span class="value">${formatDate(donor?.createdAt)}</span></td>
    </tr>
  </table>
  </div>

  <div class="block">
  <h2>DONOR BLOOD GROUP</h2>
  <table class="grid cols-2">
    <tr>
      <td><span class="label">ABO Group</span><span class="value">${show(bloodGroup.abo)}</span></td>
      <td><span class="label">RH Factor</span><span class="value">${show(bloodGroup.rh)}</span></td>
    </tr>
  </table>
  </div>

  <div class="block">
  <h2>DONOR VITALS</h2>
  <table class="grid cols-3">
    <tr>
      <td><span class="label">Pulse</span><span class="value">${vitals?.pulse ? `${show(vitals.pulse)} bpm` : "-"}</span></td>
      <td><span class="label">BP</span><span class="value">${vitals?.bloodPressure ? `${show(vitals.bloodPressure)} mmHg` : "-"}</span></td>
      <td><span class="label">Temp</span><span class="value">${vitals?.temperature ? `${show(vitals.temperature)} F` : "-"}</span></td>
    </tr>
  </table>
  </div>

  <div class="block" data-split="rows">
  <h2>DONOR MEDICAL HISTORY</h2>
  <table class="history">
    <thead><tr><th>Question</th><th class="yn">Yes/No</th><th>Question</th><th class="yn">Yes/No</th></tr></thead>
    <tbody>${historyRows}</tbody>
  </table>
  </div>

  <div class="block" data-split="rows">
  <h2>MEDICATION DEFERRAL LIST</h2>
  <table class="deferral">
    <thead>
      <tr>
        <th class="num"></th>
        <th>Generic Names</th>
        <th>Example</th>
        <th>Usually given for</th>
        <th>Deferral Period</th>
      </tr>
    </thead>
    <tbody>
      <tr><td class="num">1</td><td>Isotretinoin</td><td>Accutane R</td><td>Severe acne</td><td>1 month</td></tr>
      <tr><td class="num">2</td><td>Etretinate</td><td>Tegison R</td><td>Severe psoriasis</td><td>permanently</td></tr>
      <tr><td class="num">3</td><td>Acitretin</td><td>Neotigason R</td><td>Severe psoriasis</td><td>3 years</td></tr>
      <tr><td class="num">4</td><td>Finasteride</td><td>Proscar R, Propecia R</td><td>Prostate gland enlargement</td><td>1 month</td></tr>
      <tr><td class="num">5</td><td>Dutasteride</td><td>Avodart R</td><td>Prostate enlargement</td><td>6 months</td></tr>
    </tbody>
  </table>
  </div>

  <div class="block">
  <div class="remarks"><strong>Remarks:</strong> <span class="${donor?.remarks === "Unfit" ? "unfit" : "fit"}">${show(donor?.remarks || "Fit")}</span></div>
  </div>

  <div class="block">
  <h2>DONOR CONSENT</h2>
  <div class="consent">
    <div class="urdu" style="font-weight:700;text-align:center;">خون عطیہ کرنے کی رضامندی</div>
    <div class="urdu">میں اپنی رضامندی اور مرضی سے خون عطیہ کر رہا/رہی ہوں اور تصدیق کرتا/کرتی ہوں کہ فراہم کردہ معلومات میرے علم کے مطابق درست ہیں۔</div>
    <div class="urdu">مجھے معلوم ہے کہ میرے خون کی ضروری بیماریوں کے لیے جانچ کی جائے گی اور میں خون عطیہ کرنے کے ممکنہ اثرات سے آگاہ ہوں۔</div>
  </div>
  </div>

  <div class="block">
  <div class="signs">
    <div class="sign">
      <div class="caption">Donor Signature/Thumb</div>
      <div class="box">${donorSign ? `<img src="${donorSign}" alt="Donor signature" />` : ""}</div>
    </div>
    <div class="sign">
      <div class="caption">Doctor Signature/Thumb</div>
      <div class="box">${doctorSign ? `<img src="${doctorSign}" alt="Doctor signature" />` : ""}</div>
    </div>
  </div>
  </div>
  </div>
  <div id="pages"></div>
</body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.setAttribute(
    "style",
    "position:fixed;left:-10000px;top:0;width:210mm;height:297mm;border:0;"
  );
  document.body.appendChild(iframe);

  const printDocument = iframe.contentDocument || iframe.contentWindow?.document;
  if (!printDocument || !iframe.contentWindow) {
    document.body.removeChild(iframe);
    throw new Error("Unable to print");
  }

  printDocument.open();
  printDocument.write(html);
  printDocument.close();

  const startPrint = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    window.setTimeout(() => {
      iframe.remove();
    }, 1000);
  };

  const waitForAssets = async () => {
    const fonts = (printDocument as Document & { fonts?: { ready: Promise<unknown> } }).fonts;
    if (fonts?.ready) {
      await fonts.ready.catch(() => undefined);
    }
    const images = Array.from(printDocument.images);
    await Promise.all(
      images.map(
        image =>
          image.complete
            ? Promise.resolve()
            : new Promise(resolve => {
                image.onload = () => resolve(undefined);
                image.onerror = () => resolve(undefined);
              })
      )
    );
  };

  const fitUrduToOneLine = (doc: Document) => {
    doc.querySelectorAll<HTMLElement>(".urdu").forEach((el) => {
      const parent = el.parentElement;
      if (!parent) return;
      const available = parent.clientWidth - 8;
      let size = 15;
      el.style.fontSize = `${size}px`;
      while (size > 8 && el.scrollWidth > available) {
        size -= 0.5;
        el.style.fontSize = `${size}px`;
      }
    });
  };

  const printWhenReady = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          fitUrduToOneLine(printDocument);
          paginateDonorPrint(printDocument);
        } catch (error) {
          console.error("Donor print pagination failed:", error);
        }
        startPrint();
      });
    });
  };

  waitForAssets().then(printWhenReady).catch(printWhenReady);
};
